import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Typography
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import FilterListIcon from '@mui/icons-material/FilterList'
import AriaDatePicker from '../DateRange/AriaDatePicker'
import { DataFilters } from '../atoms'
import styles from './QASummaryFilters.module.scss'

const QASummaryFilters = ({
  filters,
  filterOptions,
  loading = false,
  onFilterChange,
  onClearFilters,
  hasActiveFilters = false
}) => {
  const handleMultiSelectChange = (field) => (event) => {
    onFilterChange({ [field]: event.target.value })
  }

  const handleSingleSelectChange = (field) => (event) => {
    onFilterChange({ [field]: event.target.value || null })
  }

  const handleDateChange = (field) => (date) => {
    if (!date) {
      onFilterChange({ [field]: null })
      return
    }
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    onFilterChange({ [field]: `${year}-${month}-${day}` })
  }

  const parseDate = (dateStr) => {
    if (!dateStr) return null
    const [year, month, day] = dateStr.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  const countActiveFilters = () => {
    let count = 0
    if (filters.campaigns.length > 0) count++
    if (filters.agents.length > 0) count++
    if (filters.dispositions.length > 0) count++
    if (filters.startDate) count++
    if (filters.endDate) count++
    if (filters.qaGrade) count++
    if (filters.failedCriteria.length > 0) count++
    return count
  }

  return (
    <Paper className={styles.root} elevation={0}>
      <Box className={styles.header}>
        <FilterListIcon className={styles.headerIcon} />
        <Typography className={styles.headerTitle}>Filters</Typography>
        {hasActiveFilters && (
          <span className={styles.activeFiltersBadge}>
            {countActiveFilters()} active
          </span>
        )}
      </Box>

      <DataFilters>
        {/* Campaign Filter */}
        <FormControl variant="outlined" size="small" className={styles.formControl}>
          <InputLabel id="qa-campaign-label">Campaign</InputLabel>
          <Select
            multiple
            labelId="qa-campaign-label"
            value={filters.campaigns}
            onChange={handleMultiSelectChange('campaigns')}
            label="Campaign"
            renderValue={(selected) => (
              <div className={styles.chips}>
                {selected.slice(0, 2).map((value) => (
                  <Chip key={value} label={value} className={styles.chip} size="small" />
                ))}
                {selected.length > 2 && (
                  <Chip label={`+${selected.length - 2}`} className={styles.chip} size="small" />
                )}
              </div>
            )}
            disabled={loading}
          >
            {filterOptions.campaigns.map((campaign) => (
              <MenuItem key={campaign} value={campaign}>
                {campaign}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Agent Filter */}
        <FormControl variant="outlined" size="small" className={styles.formControl}>
          <InputLabel id="qa-agent-label">Agent</InputLabel>
          <Select
            multiple
            labelId="qa-agent-label"
            value={filters.agents}
            onChange={handleMultiSelectChange('agents')}
            label="Agent"
            renderValue={(selected) => (
              <div className={styles.chips}>
                {selected.slice(0, 2).map((value) => (
                  <Chip key={value} label={value} className={styles.chip} size="small" />
                ))}
                {selected.length > 2 && (
                  <Chip label={`+${selected.length - 2}`} className={styles.chip} size="small" />
                )}
              </div>
            )}
            disabled={loading}
          >
            {filterOptions.agents.map((agent) => (
              <MenuItem key={agent} value={agent}>
                {agent}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range */}
        <Box className={styles.dateField}>
          <AriaDatePicker
            label="Start Date"
            value={parseDate(filters.startDate)}
            onChange={handleDateChange('startDate')}
            disabled={loading}
          />
        </Box>
        <Box className={styles.dateField}>
          <AriaDatePicker
            label="End Date"
            value={parseDate(filters.endDate)}
            onChange={handleDateChange('endDate')}
            disabled={loading}
          />
        </Box>

        {/* QA Grade Filter */}
        <FormControl variant="outlined" size="small" className={`${styles.formControl} ${styles.gradeControl}`}>
          <InputLabel id="qa-grade-label">QA Grade</InputLabel>
          <Select
            labelId="qa-grade-label"
            value={filters.qaGrade || ''}
            onChange={handleSingleSelectChange('qaGrade')}
            label="QA Grade"
            disabled={loading}
          >
            <MenuItem value="">All</MenuItem>
            {filterOptions.qaGrades.map((grade) => (
              <MenuItem key={grade} value={grade}>
                {grade}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Failed Criteria Filter */}
        <FormControl variant="outlined" size="small" className={styles.formControl}>
          <InputLabel id="qa-failed-criteria-label">Failed Criteria</InputLabel>
          <Select
            multiple
            labelId="qa-failed-criteria-label"
            value={filters.failedCriteria}
            onChange={handleMultiSelectChange('failedCriteria')}
            label="Failed Criteria"
            renderValue={(selected) => (
              <div className={styles.chips}>
                {selected.slice(0, 1).map((value) => {
                  const criterion = filterOptions.criteria.find(c => c.key === value)
                  return (
                    <Chip
                      key={value}
                      label={criterion?.label || value}
                      className={styles.chip}
                      size="small"
                    />
                  )
                })}
                {selected.length > 1 && (
                  <Chip label={`+${selected.length - 1}`} className={styles.chip} size="small" />
                )}
              </div>
            )}
            disabled={loading}
          >
            {filterOptions.criteria.map((criterion) => (
              <MenuItem key={criterion.key} value={criterion.key}>
                {criterion.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <Button
            className={styles.clearButton}
            startIcon={<ClearIcon />}
            onClick={onClearFilters}
            size="small"
          >
            Clear Filters
          </Button>
        )}
      </DataFilters>
    </Paper>
  )
}

QASummaryFilters.propTypes = {
  filters: PropTypes.shape({
    campaigns: PropTypes.array,
    agents: PropTypes.array,
    dispositions: PropTypes.array,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    qaGrade: PropTypes.string,
    failedCriteria: PropTypes.array
  }).isRequired,
  filterOptions: PropTypes.shape({
    campaigns: PropTypes.array,
    agents: PropTypes.array,
    dispositions: PropTypes.array,
    qaGrades: PropTypes.array,
    criteria: PropTypes.array
  }).isRequired,
  loading: PropTypes.bool,
  onFilterChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool
}

export default QASummaryFilters
